/*
 * UGC Infinite Wall — Ultralight
 * Zero videos on page load. Only poster images + CSS marquee.
 * Video loads on-demand when user taps play.
 */
(function () {
  'use strict';

  function initUGCWall(sectionEl) {
    const rows = sectionEl.querySelectorAll('.ugc-wall-row');
    let activeVideo = null;
    let activeWrapper = null;
    let activeRow = null;

    rows.forEach((row, rowIndex) => {
      const track = row.querySelector('.ugc-wall-track');
      if (!track) return;

      const gap = parseFloat(getComputedStyle(sectionEl).getPropertyValue('--grid-gap')) || 16;
      const items = track.querySelectorAll('.ugc-wall-item');
      if (!items.length) return;

      // Measure half-width for infinite wrap
      let totalW = 0;
      items.forEach(el => { totalW += el.offsetWidth + gap; });
      const half = totalW / 2;

      // Scroll state
      let pos = -(half * (rowIndex % 3) * 0.25); // stagger rows
      const dir = rowIndex % 2 === 0 ? -1 : 1;
      const SPEED = 30; // px/sec
      let paused = false;
      let dragging = false;
      let startX = 0, startPos = 0, lastX = 0, vel = 0, lastTime = 0;

      function wrap() {
        if (half > 0) {
          while (pos < -half) pos += half;
          while (pos > 0) pos -= half;
        }
      }

      function render() {
        track.style.transform = 'translateX(' + pos + 'px)';
      }

      // Main loop — single rAF per row
      function tick(now) {
        if (!lastTime) lastTime = now;
        const dt = Math.min((now - lastTime) / 1000, 0.1);
        lastTime = now;

        if (!paused && !dragging) {
          pos += dir * SPEED * dt;
          wrap();
          render();
        }
        requestAnimationFrame(tick);
      }

      // Drag
      function onStart(x) {
        dragging = true;
        startX = x; startPos = pos; lastX = x; vel = 0;
        row.classList.add('is-dragging');
      }
      function onMove(x) {
        if (!dragging) return;
        vel = x - lastX; lastX = x;
        pos = startPos + (x - startX);
        wrap(); render();
      }
      function onEnd() {
        if (!dragging) return;
        dragging = false;
        row.classList.remove('is-dragging');
        // Momentum
        let v = vel;
        (function coast() {
          if (Math.abs(v) < 0.3) return;
          v *= 0.95; pos += v; wrap(); render();
          requestAnimationFrame(coast);
        })();
      }

      row.addEventListener('mousedown', e => { e.preventDefault(); onStart(e.clientX); });
      window.addEventListener('mousemove', e => { onMove(e.clientX); });
      window.addEventListener('mouseup', () => { onEnd(); });
      row.addEventListener('touchstart', e => { onStart(e.touches[0].clientX); }, { passive: true });
      row.addEventListener('touchmove', e => { onMove(e.touches[0].clientX); }, { passive: true });
      row.addEventListener('touchend', () => { onEnd(); });

      // Scroll item to center
      function centerItem(item) {
        const rc = row.getBoundingClientRect();
        const ic = item.getBoundingClientRect();
        const offset = (ic.left + ic.width / 2) - (rc.left + rc.width / 2);
        const target = pos - offset;
        const from = pos;
        const start = performance.now();
        (function anim(now) {
          const t = Math.min((now - start) / 350, 1);
          const ease = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
          pos = from + (target - from) * ease;
          wrap(); render();
          if (t < 1) requestAnimationFrame(anim);
        })(performance.now());
      }

      // Click — play video on demand
      row.addEventListener('click', e => {
        if (Math.abs(lastX - startX) > 8) return; // was drag

        const wrapper = e.target.closest('.ugc-wall-media-wrapper');
        if (!wrapper) return;
        const item = wrapper.closest('.ugc-wall-item');
        const videoUrl = wrapper.dataset.videoUrl;
        if (!videoUrl || !item) return;

        e.stopPropagation();

        // If this wrapper is already playing, stop it
        if (wrapper.classList.contains('is-playing')) {
          stopActive();
          return;
        }

        // Stop any other active video first
        if (activeWrapper) stopActive();

        // Pause marquee & center
        paused = true;
        centerItem(item);
        item.classList.add('is-centered');

        // Create video element on demand
        let video = wrapper.querySelector('video');
        if (!video) {
          video = document.createElement('video');
          video.src = videoUrl;
          video.loop = true;
          video.playsInline = true;
          video.muted = false;
          video.setAttribute('playsinline', '');
          video.className = 'ugc-wall-video';
          wrapper.insertBefore(video, wrapper.querySelector('.ugc-wall-play-button'));
        }

        video.muted = false;
        video.currentTime = 0;
        video.play().then(() => {
          wrapper.classList.add('is-playing');
          activeVideo = video;
          activeWrapper = wrapper;
          activeRow = { pause: () => { paused = true; }, resume: () => { paused = false; } };
        }).catch(() => {
          // Autoplay policy fallback
          video.muted = true;
          video.play().catch(() => {});
          wrapper.classList.add('is-playing');
          activeVideo = video;
          activeWrapper = wrapper;
          activeRow = { pause: () => { paused = true; }, resume: () => { paused = false; } };
        });
      });

      wrap(); render();
      requestAnimationFrame(tick);
    });

    function stopActive() {
      if (activeVideo) {
        activeVideo.pause();
        activeVideo.remove(); // Remove video element entirely — free memory
      }
      if (activeWrapper) activeWrapper.classList.remove('is-playing');
      sectionEl.querySelectorAll('.ugc-wall-item.is-centered').forEach(el => el.classList.remove('is-centered'));
      if (activeRow) activeRow.resume();
      activeVideo = null;
      activeWrapper = null;
      activeRow = null;
    }

    document.addEventListener('click', e => {
      if (!sectionEl.contains(e.target) && activeVideo) stopActive();
    });
  }

  // Lazy init — only when section is near viewport
  const obs = new IntersectionObserver(entries => {
    entries.forEach(e => {
      if (e.isIntersecting) {
        initUGCWall(e.target);
        obs.unobserve(e.target);
      }
    });
  }, { rootMargin: '500px' });

  const go = window.requestIdleCallback || (cb => setTimeout(cb, 1));
  go(() => {
    document.querySelectorAll('.ugc-infinite-wall-section').forEach(s => obs.observe(s));
  });
})();
